import { Button, CircularProgress } from '@mui/material';
import React, { useContext, useRef, useCallback, useEffect } from 'react';

import { ChartTypeEnum } from '../../../../../model/chart_type.enum';
import { MetricKey } from '../../../../../providers/portfolio-performance/risk/risk.types';
import { COMBINED_USD_TOKEN } from '../../../../../providers/portfolio-performance/risk/risk.constants';
import { Widget } from '../../../../../components/v2/widget';
import { PortfolioPerformanceChartType } from '../../portfolio-perfomance-chart-type';
import { RiskContext } from '../../../../../providers/portfolio-performance/risk';

interface Props {
  metric: MetricKey;
  series: { time: number; value: number }[];
  loading: boolean;
  error: boolean;
  label: string;
}

export const RiskPanelGraph: React.FC<Props> = React.memo(
  ({ metric, series, loading, error, label }) => {
    const {
      subscribeToPerformanceTick,
      refetchMetric,
      fetchHistoricalChunk,
      riskTokenType,
      selectedRange,
      selectedToken,
    } = useContext(RiskContext);
    // Combined is a USD total, so it uses QUOTE precision even if the toggle
    // happens to sit on BASE.
    const effectiveType = selectedToken === COMBINED_USD_TOKEN ? 'QUOTE' : riskTokenType;
    const seriesRef = useRef<any>(null);
    const unsubRef = useRef<(() => void) | null>(null);

    const cleanupTickSubscription = useCallback(() => {
      unsubRef.current?.();
      unsubRef.current = null;
      seriesRef.current = null;
    }, []);

    const handleSeriesCreated = useCallback(
      (s: any) => {
        seriesRef.current = s;
        unsubRef.current?.();
        unsubRef.current = subscribeToPerformanceTick(({ timestamp, metrics }) => {
          const val = metrics[metric];
          if (seriesRef.current !== s || val === undefined) {
            return;
          }
          s.update({ time: timestamp, value: val });
        });
      },
      [metric, subscribeToPerformanceTick],
    );

    useEffect(() => {
      return cleanupTickSubscription;
    }, [cleanupTickSubscription]);

    useEffect(() => {
      if (loading || error || !series.length) {
        cleanupTickSubscription();
      }
    }, [cleanupTickSubscription, error, loading, series.length]);

    const renderContent = () => {
      if (loading) {
        return (
          <div className="loading-metrics">
            <CircularProgress size={24} />
          </div>
        );
      }
      if (error) {
        return (
          <div className="error-metrics">
            <span>Data loading error.</span>
            <span>Please reload or, if the error persists, try again later.</span>
            <Button onClick={() => refetchMetric(metric)}>Reload</Button>
          </div>
        );
      }
      if (!series.length) {
        return (
          <div className="no-data-metrics">
            <span>No data available.</span>
          </div>
        );
      }
      return (
        <PortfolioPerformanceChartType
          type={ChartTypeEnum.Line}
          data={series as any[]}
          precision={effectiveType === 'BASE' ? 4 : 2}
          viewportResetKey={selectedRange}
          showTooltip
          onSeriesCreated={handleSeriesCreated}
          onSeriesDisposed={cleanupTickSubscription}
          onLoadMore={(earliestTime) => fetchHistoricalChunk(metric, earliestTime)}
        />
      );
    };

    return (
      <Widget cardKey={`pp-${metric}`}>
        <Widget.Header>
          <Widget.Title title={label} />
        </Widget.Header>
        <Widget.Content>
          <div className="widget-risk-panel-graph">{renderContent()}</div>
        </Widget.Content>
      </Widget>
    );
  },
  (prev, next) =>
    prev.loading === next.loading &&
    prev.error === next.error &&
    prev.metric === next.metric &&
    prev.label === next.label &&
    prev.series.length === next.series.length &&
    prev.series[prev.series.length - 1]?.time === next.series[next.series.length - 1]?.time,
);

RiskPanelGraph.displayName = 'RiskPanelGraph';
